import { Injectable } from '@angular/core';

const SCAN_INTERVAL_MS = 30;
const SCAN_RESET_MS = 100;

export type BarcodeListenMode = 'cart' | 'capture';

export type BarcodeEnableOptions = {
    mode?: BarcodeListenMode;
};

/**
 * HID barcode scanner listener (keyboard emulation).
 *
 * Modes:
 * - **cart:** v3 behaviour — ignores form inputs except the cart `upp-search` bar; stays enabled after each scan.
 * - **capture:** one-shot assignment or discovery overlay — intercepts scans even when a form field has focus;
 *   disables automatically after one successful scan.
 *
 * Only one mode is active at a time.
 */
@Injectable({
    providedIn: 'root'
})
export class barcodeService {
    private _enabled = false;
    private _mode: BarcodeListenMode | null = null;
    private _buffer = '';
    private _lastKeyTs = 0;
    private _scannerMode = false;
    private _onScan: ((code: string) => void) | null = null;
    private _handler: ((event: KeyboardEvent) => void) | null = null;
    private _focusSink: HTMLElement | null = null;

    get IsEnabled(): boolean {
        return this._enabled;
    }

    get Mode(): BarcodeListenMode | null {
        return this._mode;
    }

    enable(callback: (code: string) => void, options: BarcodeEnableOptions = {}): void {
        const _mode = options.mode ?? 'cart';

        if (this._enabled){
            this._mode = _mode;
            this._onScan = callback;
            this._buffer = '';
            this._scannerMode = false;
            return;
        }

        this._mode = _mode;
        this._onScan = callback;
        this._handler = (event: KeyboardEvent) => this._onKeyDown(event);
        document.addEventListener('keydown', this._handler);

        if (!this._focusSink){
            const _sink = document.createElement('div');
            _sink.tabIndex = -1;
            _sink.style.position = 'fixed';
            _sink.style.left = '-9999px';
            _sink.style.top = '0';
            _sink.style.width = '1px';
            _sink.style.height = '1px';
            _sink.style.opacity = '0';
            _sink.style.pointerEvents = 'none';
            document.body.appendChild(_sink);
            this._focusSink = _sink;
        }

        this._enabled = true;
    }

    disable(): void {
        if (!this._enabled){
            return;
        }

        if (this._handler){
            document.removeEventListener('keydown', this._handler);
        }

        this._handler = null;
        this._onScan = null;
        this._mode = null;
        this._buffer = '';
        this._lastKeyTs = 0;
        this._scannerMode = false;
        this._enabled = false;

        if (this._focusSink?.parentNode){
            this._focusSink.parentNode.removeChild(this._focusSink);
        }
        this._focusSink = null;
    }

    stopCapture(): void {
        if (this._enabled && (this._mode === 'capture')){
            this.disable();
        }
    }

    private _focusedInput(): HTMLInputElement | HTMLTextAreaElement | null {
        const _active = document.activeElement;
        if (!_active){
            return null;
        }

        const _tag = _active.tagName;
        if ((_tag === 'INPUT') || (_tag === 'TEXTAREA')){
            return _active as HTMLInputElement | HTMLTextAreaElement;
        }

        return null;
    }

    private _isCartSearchFocused(_input: HTMLInputElement | HTMLTextAreaElement | null): boolean {
        if (!_input){
            return false;
        }

        return !!_input.closest('upp-search');
    }

    private _onKeyDown(event: KeyboardEvent): void {
        if (!event || (typeof event.key !== 'string')){
            return;
        }

        const _input = this._focusedInput();
        const _isCartSearch = this._isCartSearchFocused(_input);

        if ((this._mode === 'cart') && _input && !_isCartSearch){
            this._buffer = '';
            this._scannerMode = false;
            return;
        }

        const _now = Date.now();
        const _delta = _now - this._lastKeyTs;
        this._lastKeyTs = _now;

        if (event.key === 'Enter'){
            if (this._scannerMode && (this._buffer.length > 0) && this._onScan){
                const _code = this._buffer;
                this._buffer = '';
                this._scannerMode = false;

                if (_input){
                    _input.value = '';
                    event.preventDefault();
                    event.stopPropagation();
                }

                if (this._focusSink){
                    this._focusSink.focus({ preventScroll: true });
                }

                const _callback = this._onScan;
                if (this._mode === 'capture'){
                    this.disable();
                }

                _callback(_code);
            }
            else {
                this._buffer = '';
                this._scannerMode = false;
            }
            return;
        }

        if (event.key.length !== 1){
            return;
        }

        if (_delta > SCAN_RESET_MS){
            this._buffer = event.key;
            this._scannerMode = false;
            return;
        }

        this._buffer += event.key;
        if (_delta < SCAN_INTERVAL_MS){
            this._scannerMode = true;
        }
    }
}
